import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { authAPI } from '../services/api';
import { isCountrySupported } from '../utils/countryUtils';

/** Keep localStorage.countryCode aligned with profile for API headers and pricing */
const syncCountryCodeFromUser = (userData) => {
  if (!userData || typeof window === 'undefined') return;
  const c = userData.location?.country ?? userData.country;
  if (c != null && isCountrySupported(String(c))) {
    localStorage.setItem('countryCode', String(c).toUpperCase());
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage immediately (synchronous)
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });
  
  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || localStorage.getItem('customerToken');
  });
  
  const [loading, setLoading] = useState(false);
  const [isGuest, setIsGuest] = useState(() => {
    try {
      const storedToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
      const storedUser = localStorage.getItem('user');
      return !(storedToken && storedUser);
    } catch {
      return true;
    }
  });
  const [sessionActive, setSessionActive] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Activity tracking to detect user interaction
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(Date.now());
      setSessionActive(true);
    };

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Verify authentication on app load (only if we have stored data)
  useEffect(() => {
    const initAuth = async () => {
      // Drop orphaned auth fragments so visitors browse as guest, not a broken session
      const storedToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
      const storedUser = localStorage.getItem('user');
      if (storedToken && !storedUser) {
        localStorage.removeItem('token');
        localStorage.removeItem('customerToken');
        setToken(null);
      } else if (storedUser && !storedToken) {
        localStorage.removeItem('user');
        setUser(null);
      }

      if (token && user) {
        localStorage.setItem('customerToken', token);
        setSessionActive(true);
        setLastActivity(Date.now());
      }
    };

    initAuth();
  }, []);

  // Update session data when user is active
  useEffect(() => {
    if (token && user && sessionActive) {
      // Update last activity timestamp
      setLastActivity(Date.now());
    }
  }, [lastActivity, token, user, sessionActive]);

  // Automatically set isGuest based on authentication status
  // This is the single source of truth for isGuest calculation
  useEffect(() => {
    const isAuthenticated = !!(token && user && sessionActive);
    const shouldBeGuest = !isAuthenticated;
    
    // Only update if different to avoid unnecessary re-renders
    if (isGuest !== shouldBeGuest) {
      setIsGuest(shouldBeGuest);
    }
  }, [token, user, sessionActive, isGuest]);

  const login = async (identifier, password) => {
    try {
      setLoading(true);
      
    //  console.log('🔍 AuthContext: Starting login process...');
      
      // Use only real backend API
      const response = await authAPI.customerLogin(identifier, password);

   //   console.log('🔍 AuthContext: Login API response:', response);

      if (response.success) {
        const { token: authToken, ...userData } = response.data;
        
   //     console.log('✅ AuthContext: Login successful, updating state...');
   //     console.log('🔍 AuthContext: Token:', authToken ? 'EXISTS' : 'MISSING');
  //      console.log('🔍 AuthContext: User data:', userData);
        
        // Exit guest mode when successfully logging in
        setIsGuest(false);
        
        // Store token and user data
        setToken(authToken);
        setUser(userData);
        localStorage.setItem('token', authToken);
        localStorage.setItem('customerToken', authToken); // Also store as customerToken for consistency
        localStorage.setItem('user', JSON.stringify(userData));
        syncCountryCodeFromUser(userData);
        
        // Update session data
        setSessionActive(true);
        setLastActivity(Date.now());
        
        // Skip profile refresh since the endpoint doesn't exist on backend
    //    console.log('✅ AuthContext: Login successful, using user data from login response');
        
        // TODO: Implement profile refresh when backend profile endpoint is available
        // try {
        //   const profileResponse = await authAPI.getProfile();
        //   if (profileResponse.success) {
        //     console.log('✅ AuthContext: Profile refreshed after login');
        //     setUser(profileResponse.data);
        //     localStorage.setItem('user', JSON.stringify(profileResponse.data));
        //   }
        // } catch (error) {
        //   console.warn('⚠️ AuthContext: Failed to refresh profile after login:', error);
        // }
        
  //      console.log('✅ AuthContext: State updated successfully');
  //      console.log('🔍 AuthContext: isAuthenticated will be:', !!(authToken && userData && true));
        
        return { success: true };
      } else {
        console.log('❌ AuthContext: Login failed:', response.error);
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error) {
      console.error('❌ AuthContext: Login error:', error);
      return { success: false, error: error.message || 'Login failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      
      // Use real backend API
      const response = await authAPI.customerSignup(userData);

      if (response.success) {
        const { token: authToken, ...userInfo } = response.data;
        
        // Transform userInfo to ensure id/_id fields exist (backend returns customerId)
        const transformedUserInfo = {
          ...userInfo,
          id: userInfo.customerId || userInfo.id || userInfo._id,
          _id: userInfo.customerId || userInfo.id || userInfo._id
        };
        
        // Update state in correct order: token and user first, then sessionActive
        // isGuest will be automatically set by useEffect based on these values
        setToken(authToken);
        setUser(transformedUserInfo);
        setSessionActive(true);
        setLastActivity(Date.now());
        
        localStorage.setItem('token', authToken);
        localStorage.setItem('customerToken', authToken); // Also store as customerToken for consistency
        localStorage.setItem('user', JSON.stringify(transformedUserInfo));
        syncCountryCodeFromUser(transformedUserInfo);
        
        // Ensure isGuest is updated immediately (useEffect will also handle it, but this ensures consistency)
        // Don't set directly - let useEffect calculate it based on token && user && sessionActive
        
        return { success: true, message: response.message };
      } else {
        return { success: false, error: response.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.error || error.message || 'Registration failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async (clearCart = true) => {
    try {
      // Try to logout from backend
      if (token) {
        try {
          await authAPI.logout();
        } catch (error) {
          console.warn('Backend logout failed:', error.message);
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of backend response
      setUser(null);
      setToken(null);
      setSessionActive(false);
      setLastActivity(0);
      
      // Clear authentication data
      localStorage.removeItem('token');
      localStorage.removeItem('customerToken');
      localStorage.removeItem('user');
      
      // Clear cart only if explicitly requested (logout or browser kill)
      if (clearCart) {
        localStorage.removeItem('cart');
        // Dispatch cart clear event for CartContext
        window.dispatchEvent(new CustomEvent('clearCart'));
      }
    }
  };

  // Force logout (explicit logout) - clears everything including cart
  const forceLogout = async () => {
    await logout(true);
  };

  // Soft logout (browser kill) - keeps cart but clears auth
  const softLogout = async () => {
    await logout(false);
  };

  const enterGuestMode = () => {
    setIsGuest(true);
    setUser(null);
    setToken(null);
    setSessionActive(false);
    setLastActivity(0);
    localStorage.removeItem('token');
    localStorage.removeItem('customerToken');
    localStorage.removeItem('user');
  };

  const exitGuestMode = () => {
    setIsGuest(false);
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      
      // Use real backend API
      const response = await authAPI.forgotPassword(email);

      if (response.success) {
        return { success: true, message: response.message };
      } else {
        return { success: false, error: response.error || 'Password reset failed' };
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      return { 
        success: false, 
        error: error.error || error.message || 'Password reset failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setLoading(true);
      
      // Use real backend API
      const response = await authAPI.resetPassword(token, password);

      if (response.success) {
        return { success: true, message: response.message };
      } else {
        return { success: false, error: response.error || 'Password reset failed' };
      }
    } catch (error) {
      console.error('Reset password error:', error);
      return { 
        success: false, 
        error: error.error || error.message || 'Password reset failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      
      const response = await authAPI.updateProfile(profileData);
      
      if (response.success) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        syncCountryCodeFromUser(response.data);
        return { success: true, message: 'Profile updated successfully' };
      } else {
        return { success: false, error: response.error || 'Profile update failed' };
      }
    } catch (error) {
      console.error('Update profile error:', error);
      return { 
        success: false, 
        error: error.error || error.message || 'Profile update failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  const updateAvatar = async (avatarUrl) => {
    try {
      setLoading(true);
      
      // Import the file upload service
      const { fileUploadService } = await import('../services/fileUploadService');
      const response = await fileUploadService.updateAvatarUrl(avatarUrl);
      
      if (response.success) {
        // Update the user object with the new avatar
        const updatedUser = { ...user, avatar: avatarUrl };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return { success: true, message: 'Avatar updated successfully' };
      } else {
        return { success: false, error: response.error || 'Avatar update failed' };
      }
    } catch (error) {
      console.error('Update avatar error:', error);
      return { 
        success: false, 
        error: error.error || error.message || 'Avatar update failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Update user data (for profile updates)
  const updateUser = (userData) => {
    console.log('🔄 AuthContext: updateUser called with:', userData);
    const updatedUser = { ...user, ...userData };
  //  console.log('🔄 AuthContext: updated user object:', updatedUser);
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    syncCountryCodeFromUser(updatedUser);
 //   console.log('🔄 AuthContext: user updated in state and localStorage');
  };

  // Manually set authentication state (for signup with photo)
  const setAuthState = (authToken, userData) => {
    // Ensure userData has id and _id fields
    const transformedUserData = {
      ...userData,
      id: userData.customerId || userData.id || userData._id,
      _id: userData.customerId || userData.id || userData._id
    };
    
    // Update state in correct order: token and user first, then sessionActive
    // isGuest will be automatically set by useEffect based on these values
    setToken(authToken);
    setUser(transformedUserData);
    setSessionActive(true);
    setLastActivity(Date.now());
    
    localStorage.setItem('token', authToken);
    localStorage.setItem('customerToken', authToken);
    localStorage.setItem('user', JSON.stringify(transformedUserData));
    syncCountryCodeFromUser(transformedUserData);
    
    // Ensure isGuest is updated immediately (useEffect will also handle it, but this ensures consistency)
    // Don't set directly - let useEffect calculate it based on token && user && sessionActive
  };

  // Handle browser close/kill - keep cart
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't clear cart when browser is closed/killed
      // Just update last activity
      setLastActivity(Date.now());
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is hidden (tab switch, minimize, etc.) - keep cart
        setLastActivity(Date.now());
      } else {
        // Page is visible again - update activity
        setLastActivity(Date.now());
        setSessionActive(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const value = {
    user,
    token,
    loading,
    isGuest,
    sessionActive,
    lastActivity,
    login,
    register,
    logout,
    forceLogout,
    softLogout,
    enterGuestMode,
    exitGuestMode,
    forgotPassword,
    resetPassword,
    updateProfile,
    updateAvatar,
    updateUser,
    setAuthState,
    isAuthenticated: useMemo(() => {
      const auth = !!token && !!user && sessionActive;
      console.log('🔍 AuthContext: isAuthenticated calculation:', {
        hasToken: !!token,
        hasUser: !!user,
        sessionActive,
        result: auth
      });
      return auth;
    }, [token, user, sessionActive])
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 